import React from 'react';
import { useMachine } from '@xstate/react';
import { setSession, getSizeInBytes } from '~/utils';
import log from '~/log';
import { readResumableState } from './readResumableState';

type UsePersistedMachineType = {
    sessionStorageKey: string;
    services?: any;
    guards?: any;
    actions?: any;
    context?: any;
    failOnPurpose?: boolean;
    /**
     * Veto gegen einen wiederaufgenommenen State. Siehe `readResumableState`.
     */
    canResume?: (persisted: any) => boolean;
};

export function usePersistedMachine(machine: any, options: UsePersistedMachineType) {
    const { sessionStorageKey, failOnPurpose = false, canResume } = options || {};
    let persistedState = sessionStorage.getItem(sessionStorageKey);
    // Nur der erste Render entscheidet über den Startzustand — `useMachine` liest `state`
    // danach nicht mehr. Deshalb einmal auswerten und nicht bei jedem Render neu parsen.
    const resumedState = React.useMemo(
        () => readResumableState(persistedState, canResume),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );
    const [state, send, service] = useMachine(machine, {
        ...(resumedState && { state: resumedState }),
        ...options,
    });

    React.useEffect(() => {
        const subscription = service.subscribe((state: any) => {
            const hasSession = setSession(
                sessionStorageKey,
                failOnPurpose ? 'x'.repeat(5 * 1024 * 1024) : state
            );
            if (!hasSession) {
                persistedState = JSON.stringify(state);
            }
            const { value, context } = state;
            const item = sessionStorage.getItem(sessionStorageKey);
            log.debug(`usePersistedMachine.${sessionStorageKey}`, {
                value,
                context,
                state,
                ...(item && { sessionStorage: JSON.parse(item) }),
                sessionStorageSize: getSizeInBytes(sessionStorage) / 1000000,
                stateSize: getSizeInBytes(state) / 1000000,
                hasSession,
                options,
            });
        });
        return () => {
            subscription.unsubscribe();
            sessionStorage.removeItem(sessionStorageKey);
        };
    }, [service, sessionStorageKey]);

    return [state, send, service];
}
